/**
 * EarningsOverview Component
 *
 * Summary cards showing earnings totals.
 */

import React from 'react';
import type { EarningsSummary } from '../hooks';

interface EarningsOverviewProps {
  summary: EarningsSummary;
  onRequestPayout?: () => void;
}

export const EarningsOverview: React.FC<EarningsOverviewProps> = ({
  summary,
  onRequestPayout,
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="mb-8">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {/* Lifetime Earnings */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <ChartIcon className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-500">Lifetime Earnings</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(summary.totalLifetime)}
          </p>
        </div>

        {/* This Month */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <CalendarIcon className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-500">This Month</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(summary.thisMonth)}
          </p>
        </div>

        {/* Available for Payout */}
        <div className="bg-green-50 rounded-xl border border-green-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <WalletIcon className="w-5 h-5 text-green-500" />
            <span className="text-sm text-green-600">Available</span>
          </div>
          <p className="text-2xl font-bold text-green-700">
            {formatCurrency(summary.available)}
          </p>
          {summary.available >= 50 && onRequestPayout && (
            <button
              onClick={onRequestPayout}
              className="mt-3 w-full py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700"
            >
              Request Payout
            </button>
          )}
        </div>

        {/* Pending */}
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <ClockIcon className="w-5 h-5 text-amber-500" />
            <span className="text-sm text-amber-600">Pending</span>
          </div>
          <p className="text-2xl font-bold text-amber-700">
            {formatCurrency(summary.pending)}
          </p>
          <p className="text-xs text-amber-600 mt-1">
            Released after checkout
          </p>
        </div>

        {/* Paid Out */}
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircleIcon className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-blue-600">Paid Out</span>
          </div>
          <p className="text-2xl font-bold text-blue-700">
            {formatCurrency(summary.paidOut)}
          </p>
        </div>
      </div>
    </div>
  );
};

// Icons
const ChartIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
);

const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const WalletIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
    />
  </svg>
);

const ClockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const CheckCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

export default EarningsOverview;
